/**
 * /settings/data — Data & Storage transparency page.
 *
 * Shows the user every collection that stores their data, how many rows they
 * have there, and the retention policy in plain English so they can see that
 * nothing is being silently expired or deleted.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Database, ShieldCheck, Clock, Trash2 } from "lucide-react";
import { apiClient } from "../../services/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export default function DataStorage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient
      .get("/me/data-storage")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load"));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1", paddingBottom: 40 }} data-testid="data-storage-page">
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#F4F3F1",
          borderBottom: "1px solid #E7E5E1",
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4" style={{ height: 56 }}>
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="data-back"
            className="w-9 h-9 grid place-items-center rounded-full"
            style={{ background: "#FFFFFF", border: "1px solid #E7E5E1" }}
          >
            <ChevronLeft size={18} color="#1A1A1A" />
          </button>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 22, color: "#1A1A1A", letterSpacing: "-0.01em" }}>
            Data &amp; Storage
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto" style={{ padding: "20px 16px" }}>
        {/* Policy hero */}
        <div
          style={{
            background: "linear-gradient(135deg, #0FB5A6 0%, #00D4FF 100%)",
            color: "#0B0B0B",
            borderRadius: 20,
            padding: "20px 22px",
            marginBottom: 20,
          }}
          data-testid="policy-card"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <ShieldCheck size={20} />
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Your data, your rules
            </span>
          </div>
          <h1
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 800,
              fontSize: 30,
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
            }}
          >
            Everything you log is kept for life
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              marginTop: 8,
              color: "rgba(11,11,11,0.78)",
              lineHeight: 1.5,
            }}
          >
            No auto-deletion. No silent cleanup. Every meal, workout, sleep entry, FitScore day and
            check-in stays with your account — even if you log out for months — until <b>you</b>{" "}
            personally ask us to erase it.
          </p>
        </div>

        {/* Quick facts */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 22 }}>
          <FactTile
            icon={<Database size={16} color="#1A1A1A" />}
            label="Total records saved"
            value={data ? data.total_records.toLocaleString() : "—"}
            testid="fact-total"
          />
          <FactTile
            icon={<Clock size={16} color="#1A1A1A" />}
            label="Auto-expiry on your data"
            value="Never"
            testid="fact-expiry"
          />
        </div>

        {/* Collection breakdown */}
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#8A8884",
            fontWeight: 700,
            marginBottom: 10,
            paddingLeft: 4,
          }}
        >
          What we&apos;re keeping for you
        </div>

        {error && (
          <div
            data-testid="data-error"
            style={{
              padding: 14,
              borderRadius: 12,
              background: "rgba(229,72,77,0.1)",
              color: "#E5484D",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!data && !error && (
          <div style={{ padding: 20, textAlign: "center", color: "#8A8884" }}>Loading…</div>
        )}

        {data && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="data-list">
            {data.entries.map((it) => (
              <div
                key={it.collection}
                data-testid={`data-row-${it.collection}`}
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #EEECE9",
                  borderRadius: 14,
                  padding: "14px 16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: 18,
                        color: "#1A1A1A",
                        lineHeight: 1.1,
                      }}
                    >
                      {it.label}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 12,
                        color: "#8A8884",
                        marginTop: 3,
                        lineHeight: 1.4,
                      }}
                    >
                      {it.description}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 800,
                        fontSize: 22,
                        color: it.count > 0 ? "#0FB5A6" : "#B8B4AE",
                        lineHeight: 1,
                      }}
                    >
                      {it.count.toLocaleString()}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 10,
                        color: "#8A8884",
                        marginTop: 4,
                      }}
                    >
                      {it.count === 1 ? "record" : "records"}
                    </div>
                  </div>
                </div>
                {it.last_updated && (
                  <div
                    style={{
                      marginTop: 10,
                      paddingTop: 10,
                      borderTop: "1px dashed #E7E5E1",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      color: "#8A8884",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Last updated {fmtDate(it.last_updated)}</span>
                    <span style={{ color: "#0FB5A6", fontWeight: 600 }}>Kept for life</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Deletion path */}
        <div
          style={{
            marginTop: 24,
            padding: "16px 18px",
            borderRadius: 16,
            background: "#FFFFFF",
            border: "1px solid #EEECE9",
          }}
          data-testid="deletion-card"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Trash2 size={16} color="#E5484D" />
            <div
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: 18,
                color: "#1A1A1A",
              }}
            >
              Want everything deleted?
            </div>
          </div>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: "#4A4845",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            The only way anything gets removed is if you ask. Account deletion wipes your personal
            data across every collection above after a 30-day grace period, in line with India&apos;s
            DPDP Act.
          </p>
          <button
            onClick={() => navigate("/settings/privacy")}
            data-testid="go-privacy"
            style={{
              marginTop: 12,
              padding: "10px 16px",
              borderRadius: 100,
              background: "transparent",
              border: "1px solid #E5484D",
              color: "#E5484D",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Manage privacy &amp; deletion
          </button>
        </div>
      </div>
    </div>
  );
}

function FactTile({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "#FFFFFF",
        border: "1px solid #EEECE9",
        borderRadius: 14,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        {icon}
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#8A8884",
            fontWeight: 700,
          }}
        >
          {label}
        </div>
      </div>
      <div
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 800,
          fontSize: 26,
          color: "#1A1A1A",
          lineHeight: 1,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </div>
    </div>
  );
}
